import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Applica filtro alla variante default al caricamento pagina
    setTimeout(() => this.#applyInitialFilter(), 500);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Filtra le immagini della galleria per colore.
   * @param {Element} gallery - L'elemento galleria.
   * @param {string} color - Il colore selezionato (lowercase).
   */
#filterByColor(gallery, color) {
    const grid = gallery.querySelector('.media-gallery__grid');
    if (!grid) return;
    // Se non c'è colore o è la variante default, mostra tutto
    if (!color || color === 'default title') {
      Array.from(grid.children).forEach((item) => item.style.removeProperty('display'));
      return;
    }
    Array.from(grid.children).forEach((item) => {
      const alt = (item.querySelector('img')?.getAttribute('alt') || '').trim().toLowerCase();
      if (alt === color || alt.includes(color)) {
        item.style.removeProperty('display');
      } else {
        item.style.display = 'none';
      }
    });
  }

  /**
   * Applica il filtro colore alla variante selezionata al caricamento pagina.
   */
  #applyInitialFilter() {
    const variantInput = document.querySelector('input[name="id"]');
    if (!variantInput) return;
    const productEl = document.querySelector('#product-json');
    if (!productEl) return;
    try {
      const product = JSON.parse(productEl.textContent);
      const variant = product.variants?.find((v) => v.id.toString() === variantInput.value.toString());
      if (!variant?.option1) return;
      const color = variant.option1.trim().toLowerCase();
      this.#filterByColor(this, color);
    } catch (e) {}
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;
    this.replaceWith(newMediaGallery);

    // Scroll alla prima immagine
    newMediaGallery.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Filtra immagini per colore variante selezionata
    const variant = event.detail.resource;
    if (!variant?.option1) return;
    const color = variant.option1.trim().toLowerCase();
    setTimeout(() => this.#filterByColor(newMediaGallery, color), 50);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}